import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { zim } from '../../../zegoConfig';
import { loginToZego } from '../../../components/utils/zegoLogin';
import axios from '../../../api/axios';
import { sendMessageToReactNativeApp } from "../../../components/utils/sendMessageToReactNativeApp";
import { ZIM as ZimType, ZIMMessage } from 'zego-zim-web';
import { decryptFromBackend } from '../../../utils/urlDecryptor';
import HeaderWithBack from '../../../components/HeaderWithBack';

interface TeleconsultationData {
  patient_id: string;
  patient_name: string;
  practitioner_id: string;
  teleconsultation_start_time: string | null;
  doctor_assign_status: 'assigned' | 'unassigned';
}

interface UserData {
  id: string;
  person: {
    name: string;
  };
  practitioner_role_id: string;
}

type Phase = 'searching' | 'waiting_accept' | 'started';

const ConnectingDoctor: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{ 
    teleconsultationId: string; 
    token: string; 
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return '';
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error('Failed to decrypt token:', err);
      return '';
    }
  }, [token]);
  
  // States
  const [userData, setUserData] = useState<UserData | null>(null);
  const [teleconsultationData, setTeleconsultationData] = useState<TeleconsultationData | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null); // 3:30 minutes
  const [isTimeout, setIsTimeout] = useState(false);
  const timerStartTimeRef = useRef<number | null>(null);
  const WAITING_TIME = 180;
  const [phase, setPhase] = useState<Phase>('searching');
  const [fadeIn, setFadeIn] = useState(true);
  const [displayText, setDisplayText] = useState({ main: '', sub: '' });

  const phaseTexts: Record<Phase, { main: string; sub: string }> = {
    searching: {
      main: 'Mencarikan dokter terbaik untuk Anda...',
      sub: 'Mohon tunggu, sistem sedang memilih dokter yang tersedia',
    },
    waiting_accept: {
      main: 'Menghubungkan Anda ke dokter...',
      sub: 'Dokter akan terhubung ke Anda sebentar lagi',
    },
    started: {
      main: 'Dokter siap!',
      sub: 'Mengalihkan ke ruang konsultasi...',
    },
  };

  useEffect(() => {
    const savedStartTime = localStorage.getItem(`timer_start_${teleconsultationId}`);

    if (savedStartTime) {
      const startTime = parseInt(savedStartTime, 10);
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, WAITING_TIME - elapsedSeconds);
      console.log(`⏱️ Timer berlanjut: ${remaining} detik tersisa`);
      timerStartTimeRef.current = startTime;
      setTimeLeft(remaining);
    } else {
      const now = Date.now();
      timerStartTimeRef.current = now;
      localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
      
      console.log('⏱️ Timer dimulai baru: 180 detik');
      axios.get("waiting-acceptance-time", {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      })
        .then(res => {
          setTimeLeft(res.data.time);
        })
        .catch(() => {
          setTimeLeft(WAITING_TIME);
        });
    }
  }, [teleconsultationId, decryptedToken]);
  
  useEffect(() => {
    if (timeLeft === null || timerStartTimeRef.current === null) return;

    if (timeLeft <= 0) {
      console.log("⏰ Timer habis");
      setIsTimeout(true);
      localStorage.removeItem(`timer_start_${teleconsultationId}`);
      return;
    }

    const timer = setInterval(() => {
      if (timerStartTimeRef.current === null) return;

      const elapsedSeconds = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
      const remaining = Math.max(0, WAITING_TIME - elapsedSeconds);

      setTimeLeft(remaining);

      if (remaining <= 0) {
        setIsTimeout(true);
        localStorage.removeItem(`timer_start_${teleconsultationId}`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, teleconsultationId]);

  // Refs
  const previousUserDokterRef = useRef<string | null>(null);
  const isCancelledRef = useRef(false);

  // Utility functions
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const handleNavigationError = useCallback((error: any) => {
    if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      navigate('/not-found');
    } else if (error.status === 403) {
      console.log('You Are Not Authorized to access this resource!') 
      navigate('/forbidden');
    } else {
      console.error('Unexpected error:', error);
    }
  }, [navigate]);

  // API calls
  const fetchUserData = useCallback(async (): Promise<UserData | null> => {
    if (!decryptedToken) return null;
    
    try {
      const response = await axios.post('users', null, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      
      const data = response.data.data;
      localStorage.setItem('userData', JSON.stringify(data));
      console.log('✅ User data berhasil di-fetch:', data);
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, handleNavigationError]);

  const fetchTeleconsultationData = useCallback(async (): Promise<TeleconsultationData | null> => {
    console.log('fetchTeleconsultationData')
    if (!decryptedToken || !teleconsultationId) return null;
    
    try {
      const response = await axios.get(`teleconsult/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      
      const data = response.data;
      localStorage.setItem('teleconsultationData', JSON.stringify(data));
      console.log('✅ Teleconsultation data berhasil di-fetch:', data);
      return data;
    } catch (error) {
      console.log('Teleconsultation data gagal di-fetch:', error);
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, teleconsultationId, handleNavigationError]);

  // Validation functions
  const isValidUserData = (data: any): data is UserData => {
    return data && 
           data.id && 
           data.person?.name && 
           data.practitioner_role_id;
  };

  const isValidTeleconsultationData = (data: any): data is TeleconsultationData => {
    return data && 
           data.patient_id && 
           data.patient_name && 
           data.practitioner_id;
  };

  // ZIM setup functions
  const setupConversationListener = useCallback((targetUserId: string) => {
    console.log('🎧 Menyiapkan listener ZIM untuk user:', targetUserId);

    // Clear existing listeners
    zim.off('messageReceived');
    zim.off('conversationChanged');
    zim.off('peerMessageReceived');

    zim.on(
      'peerMessageReceived',
      (
        zim: any,
        { messageList, fromConversationID }: { messageList: ZIMMessage[]; fromConversationID: string }
      ) => {
        console.log('peerMessageReceived', messageList, fromConversationID);
        const acceptedMessages = messageList.filter(
          (msg: any) => 
            typeof msg.message === 'string' &&
            msg.message.startsWith('accepted') &&
            msg.message.includes(teleconsultationId || '')
        );

        if (acceptedMessages.length > 0 && teleconsultationId) {
          console.log('✅ Pesan konfirmasi ditemukan! Redirect ke chat...');
          navigate(`/chat/${teleconsultationId}/${token}`, { replace: true });
        }

      }
    );

    // Message received listener
    zim.on('messageReceived', async (zimInstance: any, messageList: any[]) => {
      console.log('📨 Pesan baru diterima:', messageList);
      console.log('Teleconsultation ID:', teleconsultationId);

      const acceptedMessages = messageList.filter(
        (msg: any) =>
          typeof msg.message === 'string' &&
          msg.message.startsWith('accepted') &&
          msg.message.includes(teleconsultationId || '')
      );

      if (acceptedMessages.length > 0 && teleconsultationId) {
        console.log('✅ Pesan konfirmasi ditemukan! Redirect ke chat...');
        navigate(`/chat/${teleconsultationId}/${token}`, { replace: true });
      }
    });

    // Conversation changed listener (optional)
    zim.on('conversationChanged', async (conversationList: any) => {
      console.log('💬 Percakapan berubah:', conversationList);
      const config = { nextMessage: 0, count: 30, reverse: true };
      try {
        const chatHistory = await zim.queryHistoryMessage(targetUserId, 0, config);
        const acceptedMessages = chatHistory.messageList.filter(
          (msg: any) => typeof msg.message === 'string' && msg.message.startsWith('accepted')
        );

        acceptedMessages.forEach((msg: any) => {
          const id = msg.message.split('|')[1]?.trim();
          if (id === teleconsultationId) {
            console.log('✅ Pesan konfirmasi ditemukan, pindah ke chat...');
            navigate(`/chat/${teleconsultationId}/${token}`, { replace: true });
          }
        });
      } catch (err) {
        console.error('❌ Gagal ambil riwayat pesan:', err);
      }
    });
  }, [teleconsultationId, navigate]);

 
  const loginAndSetupZIM = useCallback(async () => {
    console.log('loginAndSetupZIM called');
    if (!teleconsultationData || !process.env.REACT_APP_ZEGOCLOUD_APP_ENV) {
      console.log('⚠️ Teleconsultation data atau environment variable tidak tersedia');
      return;
    }

    if (!isValidTeleconsultationData(teleconsultationData)) {
      console.log('⚠️ Teleconsultation data tidak lengkap, skip login');
      return;
    }

    try {
      const envSuffix = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || '';
      const userId = `LMS${teleconsultationData.patient_id}${envSuffix}`;
      const username = teleconsultationData.patient_name;
      const practitionerId = `LMS${teleconsultationData.practitioner_id}${envSuffix}`;

      console.log('🔑 Login ZIM:', { userId, username, practitionerId });
      
      const loginSuccess = await loginToZego(userId, username);
      console.log('✅ ZIM login result:', loginSuccess);

      if (loginSuccess) {
        console.log('🎯 Login berhasil, menyiapkan listener...');
        console.log('🎯 Previous userDokter:', practitionerId);
        setupConversationListener(practitionerId);
        previousUserDokterRef.current = practitionerId;
        console.log('🎯 Login dan listener berhasil di-setup');
      }
    } catch (error) {
      console.error('❌ Login ZIM gagal:', error);
    }
  }, [teleconsultationData, setupConversationListener]);

  // Load initial data from sessionStorage or API
  const loadInitialData = useCallback(async () => {
    try {
      // Try to get data from sessionStorage first
      const savedUserData = localStorage.getItem('userData');
      const savedTeleconsultationData = localStorage.getItem('teleconsultationData');

      console.log('ehem', savedTeleconsultationData)
      
      let userDataValid = false;
      let teleconsultationDataValid = false;

      // Validate user data
      if (savedUserData) {
        try {
          const parsedUserData = JSON.parse(savedUserData);
          if (isValidUserData(parsedUserData)) {
            console.log('✅ UserData dari session valid');
            setUserData(parsedUserData);
            userDataValid = true;
          }
        } catch (e) {
          console.warn('⚠️ UserData dari session corrupt');
        }
      }

      // Validate teleconsultation data
      if (savedTeleconsultationData) {
        try {
          const parsedTeleconsultationData = JSON.parse(savedTeleconsultationData);
          if (isValidTeleconsultationData(parsedTeleconsultationData)) {
            console.log('✅ TeleconsultationData dari session valid');
            setTeleconsultationData(parsedTeleconsultationData);
            teleconsultationDataValid = true;
          }
        } catch (e) {
          console.warn('⚠️ TeleconsultationData dari session corrupt');
        }
      }

      // Fetch missing or invalid data
      if (!userDataValid || !teleconsultationDataValid) {
        console.log('🔄 Fetching missing data...');
        
        const promises = [];
        if (!userDataValid) promises.push(fetchUserData());
        // if (!teleconsultationDataValid) promises.push(fetchTeleconsultationData());
        promises.push(fetchTeleconsultationData()); // Fetch to get latest info
        
        const results = await Promise.all(promises);
        
        let fetchIndex = 0;
        if (!userDataValid && results[fetchIndex]) {
          setUserData(results[fetchIndex] as UserData);
          fetchIndex++;
        }
        if (!teleconsultationDataValid && results[fetchIndex]) {
          setTeleconsultationData(results[fetchIndex] as TeleconsultationData);
        }
      }
      

      setIsDataLoaded(true);
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      setIsDataLoaded(true); // Set true to prevent infinite loading
    }
  }, [fetchUserData, fetchTeleconsultationData]);

  // Effects
  useEffect(() => {
    loadInitialData();

    return () => {
      isCancelledRef.current = true;
    };
  }, [loadInitialData]);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const updatePhaseWithFade = (newPhase: Phase) => {
    setFadeIn(false);
    setTimeout(() => {
      setDisplayText(phaseTexts[newPhase]);
      setPhase(newPhase);
      setFadeIn(true);
    }, 400);
  };

  useEffect(() => {
    if (!isDataLoaded || !teleconsultationId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`teleconsult/${teleconsultationId}/assign-status`, {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        });

        const { phase: serverPhase } = res.data;

        if (serverPhase === 'started') {
          updatePhaseWithFade('started');
          if (pollingRef.current) clearInterval(pollingRef.current);

          localStorage.removeItem(`timer_start_${teleconsultationId}`);

          setTimeout(() => navigate(`/chat/${teleconsultationId}/${token}`, { replace: true }), 1000);
          return;
        }

        if (serverPhase === 'waiting_accept' && phase !== 'waiting_accept') {
          updatePhaseWithFade('waiting_accept');
          loginAndSetupZIM();
          return;
        }

        if (serverPhase === 'searching') {
          updatePhaseWithFade('searching');
        }

      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Set teks awal
    setDisplayText(phaseTexts['searching']);

    poll(); // langsung poll sekali
    pollingRef.current = setInterval(poll, 5000); // poll tiap 5 detik

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isDataLoaded, teleconsultationId]);
  

  // Timer fallback effect
  // useEffect(() => {
  //   if (timeLeft <= 0 && teleconsultationId) {
  //     console.log('⏰ Timer habis, redirect ke chat fallback');
  //     navigate(`/chat/${teleconsultationId}/${token}`);
  //     return;
  //   }

  //   const timer = setInterval(() => {
  //     setTimeLeft((prev) => prev - 1);
  //   }, 1000);

  //   return () => clearInterval(timer);
  // }, [timeLeft, navigate, teleconsultationId]);

  // Effect 2 — HANYA logika reassign, tanpa setInterval
  // useEffect(() => {
  //   if (timeLeft === null) return;
  //   if (phase === 'started') return; // Guard: jangan reassign jika sudah di chat

  //   if (timeLeft <= 0 && (phase === 'searching' || phase === 'waiting_accept')) {
  //     updatePhaseWithFade('searching');
  //     const now = Date.now();
  //     timerStartTimeRef.current = now;
  //     localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
  //     setTimeLeft(WAITING_TIME);

  //     axios.post(`teleconsult/${teleconsultationId}/reassign-doctor`, {}, {
  //       headers: { Authorization: `Bearer ${decryptedToken}` },
  //     }).catch(err => console.error('Reassign gagal:', err));
  //   }
  //   // ❌ Tidak ada setInterval di sini sama sekali
  // }, [timeLeft, phase, teleconsultationId]);

  // Handler tombol
  const handleRetry = () => {
    // setTimeLeft(210); // reset timer
    setIsTimeout(false);
    // loginAndSetupZIM(); // coba login ulang
    createConsultation();
  };

  const handleBack = () => {
    // Tambahkan cleanup di sini
    localStorage.removeItem(`timer_start_${teleconsultationId}`);
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    sendMessageToReactNativeApp({ action: 'goBack' });
    navigate('/home', { replace: true }); // replace agar ConnectingDoctor hilang dari history
  };

  //create consultation
  const createConsultation = useCallback(async () => {
    const response = await axios.get(`user/doctor-code/${teleconsultationId}`, {
      headers: { Authorization: `Bearer ${decryptedToken}` },
    });
    const doctorCode = response.data;
    try {
      const response = await axios.post(
        "user/consultation",
        {
          doctorId: doctorCode,
          mediaType: "chat",
          intent: 'retry',
        },
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        }
      );
  
      if (response.data?.code === 200) {
        const roomUrl = response.data.data?.roomUrl;
        if (roomUrl) {
          window.location.href = roomUrl;
        } else {
          console.error("Room URL tidak ditemukan di response");
        }
      } else {
        console.error("Request gagal:", response.data?.message);
      }
    } catch (error) {
      console.error("Error saat loginAndSetupZIM:", error);
    }
  }, []);



  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex flex-col items-center justify-between">
      <div className="fixed top-0 left-0 right-0 z-[10000]">
        <HeaderWithBack title="Connect Doctor" url="/home" />
      </div>
      <div className="flex flex-col items-center justify-center flex-grow mt-4 px-4 text-center">
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {(!isTimeout || phase === 'searching' || phase === 'waiting_accept') && (
            <div className="flex flex-col items-center gap-4">
              <img 
                src="/logo-linksehat.png" 
                alt="Loading" 
                className="w-20 h-20 animate-pulse" 
              />
                  {timeLeft !== null && (
                    <p className="text-2xl font-semibold text-black">
                      {formatTime(timeLeft)}
                    </p>
                  )}


              <p className={`font-bold text-xl transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {displayText.main}
              </p>
              <p className={`text-sm text-gray-500 transition-opacity duration-500 delay-100 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {displayText.sub}
              </p>
            </div>
          )}
          {isTimeout && phase !== 'searching' && phase !== 'waiting_accept' && (
              <div className="absolute inset-0 bg-black/40 flex items-end z-50">
                <div className="bg-white rounded-t-2xl w-full p-6 relative">
                  {/* Tombol close */}
                  <button
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-lg"
                    onClick={handleBack}
                  >
                    ✕
                  </button>

                  {/* Icon merah */}
                  <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-4xl">✖</span>
                  </div>

                  {/* Judul */}
                  <h2 className="mt-4 text-lg font-bold text-center text-black">
                    Gagal Menghubungkan
                  </h2>

                  {/* Deskripsi */}
                  <p className="text-sm text-gray-500 text-center mt-1">
                    Saat ini kami gagal menghubungkan Anda ke dokter. Silakan coba lagi.
                  </p>

                  {/* Tombol */}
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={handleBack}
                      className="flex-1 py-2 rounded-full border border-teal-500 text-teal-500"
                    >
                      Kembali
                    </button>
                    <button
                      onClick={handleRetry}
                      className="flex-1 py-2 rounded-full bg-teal-500 text-white"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ConnectingDoctor;
